import React from 'react';
import { Link } from 'react-router-dom';
import { Network, CheckCircle, Wifi, Shield, Zap, Cloud, ArrowRight, Phone, Radio } from 'lucide-react';

export default function Conectividad() {
  const caracteristicas = [
    {
      icon: Wifi,
      titulo: 'Redes Inteligentes con IA',
      descripcion: 'HPE Aruba Networking con Mist AI para optimización automática y resolución proactiva de problemas.'
    },
    {
      icon: Shield,
      titulo: 'Seguridad Perimetral',
      descripcion: 'Firewalls Juniper SRX y SASE para protección integral en el borde de la red.'
    },
    {
      icon: Cloud,
      titulo: 'Gestión Centralizada',
      descripcion: 'Aruba Central Cloud para administrar toda la infraestructura de red desde un solo panel.'
    },
    {
      icon: Zap,
      titulo: 'Alta Disponibilidad',
      descripcion: 'Switches y Access Points empresariales con redundancia y failover automático.'
    }
  ];

  const soluciones = [
    {
      categoria: 'HPE Aruba Networking Central',
      descripcion: 'Plataforma cloud-native para gestión unificada de redes empresariales',
      componentes: [
        'Dashboard centralizado para toda la red',
        'Configuración zero-touch de dispositivos',
        'Monitoreo en tiempo real con analytics',
        'Automatización de políticas de red'
      ],
      casos: 'Campus empresariales, oficinas distribuidas, retail, hospitales, educación'
    },
    {
      categoria: 'Mist AI & Marvis AI',
      descripcion: 'Inteligencia artificial para redes que aprende y se auto-optimiza',
      componentes: [
        'Detección automática de anomalías',
        'Asistente virtual Marvis para troubleshooting',
        'Optimización de RF automática',
        'Predicción proactiva de fallos'
      ],
      casos: 'Ambientes con alta densidad de usuarios, Wi-Fi crítico para operaciones, experiencia de usuario premium'
    },
    {
      categoria: 'HPE Aruba Networking SASE',
      descripcion: 'Secure Access Service Edge para proteger acceso remoto y cloud',
      componentes: [
        'SD-WAN integrado con seguridad',
        'Zero Trust Network Access (ZTNA)',
        'Cloud Web Security (SWG)',
        'Firewall as a Service (FWaaS)'
      ],
      casos: 'Trabajo híbrido, sucursales remotas, acceso seguro a SaaS, protección de usuarios móviles'
    },
    {
      categoria: 'Juniper SRX Firewall',
      descripcion: 'Firewalls de próxima generación para seguridad perimetral avanzada',
      componentes: [
        'Inspección profunda de paquetes (DPI)',
        'IPS/IDS integrado',
        'VPN IPsec y SSL',
        'Protección contra amenazas avanzadas'
      ],
      casos: 'Data centers, perímetros de red, segmentación de seguridad, conexiones site-to-site'
    },
    {
      categoria: 'HPE Aruba Switch Series & Access Points',
      descripcion: 'Infraestructura de switching y wireless de nivel empresarial',
      componentes: [
        'Switches CX Series con PoE++',
        'Access Points Wi-Fi 6E y Wi-Fi 7',
        'Gestión basada en cloud o on-premise',
        'QoS y segmentación de red'
      ],
      casos: 'Backbone de red, distribución de campus, acceso de usuarios, IoT empresarial'
    }
  ];

  const beneficios = [
    'Reducción de 90% en tiempo de resolución de problemas con Marvis AI',
    'Hasta 70% menos gastos operativos con gestión cloud centralizada',
    'Wi-Fi 6E con velocidades de hasta 9.6 Gbps',
    'Seguridad Zero Trust integrada en toda la red',
    'Implementación 5x más rápida con zero-touch provisioning',
    'Cobertura y capacidad optimizada automáticamente con IA',
    '99.99% de disponibilidad de red con arquitectura resiliente'
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1544197150-b99a580bb7a8?q=80&w=2370)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-green-900/90 via-teal-900/85 to-green-900/90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block bg-green-600/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <span className="text-green-300 font-semibold">HPE Aruba Networking + Mist AI + Juniper</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Conectividad Empresarial Inteligente
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8">
              Redes corporativas con inteligencia artificial, seguridad integrada y gestión cloud unificada
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/contacto" 
                className="bg-green-600 text-white px-8 py-4 rounded-lg hover:bg-green-700 transition-colors text-lg font-semibold inline-flex items-center justify-center"
              >
                Solicitar Evaluación de Red <ArrowRight className="ml-2" />
              </Link>
              <a 
                href="#soluciones" 
                className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg hover:bg-white/20 transition-colors text-lg font-semibold inline-flex items-center justify-center border border-white/30"
              >
                Ver Soluciones
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introducción */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Redes que se optimizan solas
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                La <strong>conectividad moderna</strong> va más allá de switches y access points. Con 
                <strong> HPE Aruba Networking</strong> y <strong>Mist AI</strong>, obtienes una red que aprende 
                de su entorno, predice problemas antes de que ocurran y se auto-optimiza para dar la mejor 
                experiencia a usuarios y aplicaciones.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Nuestras soluciones integran switching, wireless Wi-Fi 6E/7, seguridad perimetral con 
                <strong> Juniper SRX</strong>, y SASE para trabajo híbrido. Todo gestionado desde 
                <strong> Aruba Central</strong>, una plataforma cloud que unifica la administración de 
                toda tu infraestructura de red.
              </p>
              <div className="bg-green-50 border-l-4 border-green-600 p-6 rounded-lg">
                <p className="text-gray-800 font-semibold mb-2">
                  <Network className="inline mr-2 text-green-600" size={24} />
                  Marvis: El Asistente Virtual de Red
                </p>
                <p className="text-gray-600">
                  Marvis AI es como tener un experto de redes disponible 24/7. Responde preguntas en 
                  lenguaje natural ("¿por qué el Wi-Fi está lento en el edificio 3?") y ejecuta 
                  diagnósticos automáticamente.
                </p>
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1551808525-51a94da548ce?q=80&w=2070" 
                alt="Network Infrastructure"
                className="rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Características */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Capacidades Clave
            </h2>
            <p className="text-xl text-gray-600">
              Tecnología de red de última generación
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {caracteristicas.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-shadow">
                  <div className="bg-green-50 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                    <Icon className="text-green-600" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.titulo}</h3>
                  <p className="text-gray-600">{item.descripcion}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Soluciones */}
      <section id="soluciones" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Portafolio de Conectividad
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Desde wireless hasta seguridad perimetral
            </p>
          </div>
          <div className="space-y-8">
            {soluciones.map((solucion, index) => (
              <div 
                key={index}
                className="bg-gradient-to-r from-green-50 to-white border border-green-200 rounded-xl p-8 hover:shadow-xl transition-all"
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="bg-green-100 p-3 rounded-lg">
                        <Radio className="text-green-600" size={28} />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">{solucion.categoria}</h3>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-4 text-lg">{solucion.descripcion}</p>
                    <div className="mb-4">
                      <span className="text-sm font-semibold text-gray-700">Componentes incluidos:</span>
                      <ul className="mt-2 space-y-2">
                        {solucion.componentes.map((componente, idx) => (
                          <li key={idx} className="flex items-center space-x-2 text-gray-600">
                            <CheckCircle className="text-hpe-green flex-shrink-0" size={16} />
                            <span>{componente}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-6 border border-green-200">
                    <h4 className="font-semibold text-gray-900 mb-3">Casos de uso ideales:</h4>
                    <p className="text-gray-600 text-sm">{solucion.casos}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section className="py-20 bg-gradient-to-br from-green-600 to-teal-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-4xl font-bold mb-6">
                Resultados Medibles
              </h2>
              <p className="text-xl text-green-100 mb-8">
                Organizaciones que implementan conectividad inteligente con HPE Aruba experimentan:
              </p>
              <ul className="space-y-4">
                {beneficios.map((beneficio, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="text-yellow-300 flex-shrink-0 mt-1" size={24} />
                    <span className="text-lg text-green-50">{beneficio}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2070" 
                alt="Network Performance"
                className="rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Network className="text-green-600 mx-auto mb-6" size={64} />
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Moderniza tu red empresarial
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Como partner certificado de HPE Aruba y Juniper, ALLTI diseña e implementa redes 
            inteligentes con IA, seguridad integrada y gestión simplificada.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contacto" 
              className="bg-green-600 text-white px-8 py-4 rounded-lg hover:bg-green-700 transition-colors text-lg font-semibold inline-flex items-center justify-center"
            >
              <Phone className="mr-2" size={20} />
              Contactar a Expertos en Redes
            </Link>
            <Link 
              to="/casos-de-uso" 
              className="bg-gray-100 text-gray-900 px-8 py-4 rounded-lg hover:bg-gray-200 transition-colors text-lg font-semibold inline-flex items-center justify-center"
            >
              Ver Implementaciones
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
